import { NgTemplateOutlet } from '@angular/common';
import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { buildInfo } from '../../../buildinfo';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { QaBackdoorService } from '../../core/services/qa-backdoor.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { InputComponent } from '../../shared/components/form-controls/input/input.component';
import { QaBackdoorModalComponent } from '../../shared/components/qa-backdoor-modal/qa-backdoor-modal.component';
import { TabStripComponent } from '../../shared/components/tab-strip/tab-strip.component';
import { TabComponent } from '../../shared/components/tab-strip/tab.component';
import { EnvPrefixPipe } from '../../shared/pipes/env-prefix.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { nativePlatform } from '../../core/utils/native-shell';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslatePipe,
    EnvPrefixPipe,
    FormsModule,
    InputComponent,
    TabStripComponent,
    TabComponent,
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.css',
})
export class LoginComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private logger = inject(LoggerService).getLogger('auth');
  private translate = inject(TranslationService);
  private modalService = inject(ModalService);
  private toast = inject(ToastService);
  private qaBackdoor = inject(QaBackdoorService);

  // TEMPORARY: platform label above the Google button (see login.component.html)
  protected readonly nativePlatform = nativePlatform();
  /** Build time in the device's local timezone + commit, to confirm which bundle is running */
  protected readonly nativeBuild = `${new Date(buildInfo.timestamp).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })} (${buildInfo.gitHash})`;

  appName = this.translate.translate('app.name');
  appDescription = this.translate.translate('app.description');
  debugInfo = {
    clientId: environment.oauth.google.clientId,
    redirectUri: environment.oauth.google.redirectUri,
    apiUrl: environment.apiUrl,
    oauthUrl: `${environment.apiUrl.replace('/api', '')}/auth/google`,
  };

  // Test auth properties -- the tab only appears once a valid QA backdoor
  // token has been entered via the hidden logo-tap trigger (see onLogoTap()).
  // enableTestAuth alone (a build-time flag) is not enough on shared
  // environments like integration, where it's true for everyone.
  enableTestAuth = signal(environment.enableTestAuth && this.qaBackdoor.hasToken());
  testEmail = signal<string>('');
  private readonly LAST_TEST_EMAIL_KEY = 'last_test_email';
  private userStorage = inject(UserStorageService);

  // Tab selection
  activeTab = signal<string>('google');

  // Hidden QA backdoor unlock: press and hold the logo to open the "enter QA
  // key" modal. Long-press (a single sustained pointer-down) rather than
  // repeated taps, since rapid taps on the same spot get misread as a
  // gesture by mobile browsers/trackpads (double-tap-zoom, swipe-back) and
  // can navigate away before the tap count is ever reached.
  private logoPressTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly LOGO_PRESS_DURATION_MS = 1500;

  constructor() {
    // Load last used test email from global localStorage (device-level for test convenience)
    const lastEmail = this.userStorage.getGlobalItem(this.LAST_TEST_EMAIL_KEY);
    if (lastEmail) {
      // Strip @test.com suffix for display (user only types the username part in UI)
      const usernameOnly = lastEmail.replace(/@test\.com$/i, '');
      this.testEmail.set(usernameOnly);
    }

    // Reactively redirect to returnUrl or home if user becomes authenticated
    // Angular signals only trigger effects when values CHANGE, so this should only run once
    // when authentication goes from false -> true
    effect(() => {
      const isAuth = this.authService.isAuthenticated();
      this.logger.debug(`Effect triggered, isAuthenticated=${isAuth}`);

      if (isAuth) {
        // Check if user has an organization by decoding JWT
        const token = this.authService.getToken();
        if (!token) {
          this.logger.warn('No token found despite authentication - logging out');
          this.authService.logout();
          return;
        }

        const payload = this.authService.decodeJwt(token);
        const hasOrganization = !!payload?.customer_id;

        if (!hasOrganization) {
          // User exists but has no organization - send to signup
          this.logger.info('User has no organization - redirecting to /signup');
          this.router.navigate(['/signup']);
        } else {
          // User has organization - proceed to returnUrl or home
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
          this.logger.info(`User authenticated with org, navigating to: ${returnUrl}`);
          this.router.navigateByUrl(returnUrl);
        }
      } else {
        this.logger.debug('User not yet authenticated');
      }
    });
  }

  login(): void {
    this.logger.debug('OAuth Debug Info:');
    this.logger.debug('Client ID:', this.debugInfo.clientId);
    this.logger.debug('Redirect URI:', this.debugInfo.redirectUri);
    this.logger.debug('API URL:', this.debugInfo.apiUrl);
    this.logger.debug('OAuth URL:', this.debugInfo.oauthUrl);
    this.authService.login();
  }

  async testLogin(): Promise<void> {
    const email = this.testEmail().trim();

    if (!email) {
      return;
    }

    // Reject emails that contain @ but don't end with @test.com (case-insensitive)
    // This prevents: user@example.com -> user@example.com@test.com
    if (email.includes('@') && !email.toLowerCase().endsWith('@test.com')) {
      return;
    }

    // Strip @test.com suffix if present (case-insensitive), then add it back to normalize
    // This prevents double-suffixing (user2@test.com -> user2@test.com@test.com)
    const emailPrefix = email.replace(/@test\.com$/i, '');
    const testEmailAddress = `${emailPrefix}@test.com`;

    // Save email to global localStorage for convenience (device-level for test)
    this.userStorage.setGlobalItem(this.LAST_TEST_EMAIL_KEY, testEmailAddress);

    await this.authService.testLogin(testEmailAddress);
  }

  /** Hidden trigger: press and hold the logo to unlock the QA backdoor. */
  onLogoPressStart(): void {
    if (this.logoPressTimer) {
      clearTimeout(this.logoPressTimer);
    }
    this.logoPressTimer = setTimeout(() => {
      this.logoPressTimer = null;
      this.openQaBackdoorModal();
    }, LoginComponent.LOGO_PRESS_DURATION_MS);
  }

  onLogoPressEnd(): void {
    if (this.logoPressTimer) {
      clearTimeout(this.logoPressTimer);
      this.logoPressTimer = null;
    }
  }

  private async openQaBackdoorModal(): Promise<void> {
    const config: ModalConfig = {
      component: QaBackdoorModalComponent,
      title: 'QA Backdoor',
      size: 'small',
      // The modal opens mid-gesture, while the mouse/touch that triggered the
      // long-press is still down. The eventual mouseup/touchend lands on the
      // backdrop (now covering the logo) and would otherwise register as an
      // "outside click" and immediately close the modal it just opened.
      closeOnClickOutside: false,
      actionButtons: [
        {
          id: 'cancel',
          label: 'Cancel',
          variant: 'text',
          onClick: async () => {
            this.modalService.closeModal({ action: 'cancel' });
          },
        },
        {
          id: 'unlock',
          label: 'Unlock',
          variant: 'primary',
          onClick: async () => {
            const instance = this.modalService.getComponentInstance() as QaBackdoorModalComponent;
            const token = instance?.getToken();

            if (!token) {
              return;
            }

            const valid = await this.qaBackdoor.validateAndSave(token);
            if (valid) {
              this.enableTestAuth.set(environment.enableTestAuth && this.qaBackdoor.hasToken());
              this.toast.success('QA backdoor unlocked');
              this.modalService.closeModal({ action: 'unlock' });
            } else {
              this.toast.error('Invalid QA key');
            }
          },
        },
      ],
    };

    await this.modalService.openModal(config);
  }
}
